import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Pencil, MapPin, Phone, User, Percent } from 'lucide-react';
import { locationsApi } from '@/api/locationsApi';
import { inventoryApi } from '@/api/inventoryApi';
import { salesApi } from '@/api/salesApi';
import { transfersApi } from '@/api/transfersApi';
import { auditApi } from '@/api/auditApi';
import { DetailPageLayout } from '@/components/detail/DetailPageLayout';
import { EntityLink } from '@/components/detail/EntityLink';
import { StockTable } from '@/components/detail/StockTable';
import { HistoryTable } from '@/components/detail/HistoryTable';
import { Button } from '@/components/common/Button';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { useAuthStore } from '@/store/authStore';
import { useState } from 'react';
import { AddLocationModal } from '@/components/locations/AddLocationModal';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'stock', label: 'Stock' },
  { id: 'sales', label: 'Sales' },
  { id: 'transfers', label: 'Transfers' },
  { id: 'activity', label: 'Activity' },
];

export function LocationDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const user = useAuthStore((s) => s.user);
  const canViewAudit = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const [showEdit, setShowEdit] = useState(false);

  const { data: location, isLoading, isError, refetch } = useQuery({
    queryKey: ['location', id],
    queryFn: () => locationsApi.getById(id!),
    enabled: !!id,
  });

  const { data: stock, isLoading: stockLoading } = useQuery({
    queryKey: ['inventory-location', id],
    queryFn: () => inventoryApi.getByLocation(id!),
    enabled: !!id && (activeTab === 'stock' || activeTab === 'overview'),
  });

  const { data: sales, isLoading: salesLoading } = useQuery({
    queryKey: ['sales-location', id],
    queryFn: () => salesApi.getByLocation(id!),
    enabled: !!id && (activeTab === 'sales' || activeTab === 'overview'),
  });

  const { data: transfers, isLoading: transfersLoading } = useQuery({
    queryKey: ['transfers-location', id],
    queryFn: () => transfersApi.getByLocation(id!),
    enabled: !!id && activeTab === 'transfers',
  });

  const { data: auditLogs } = useQuery({
    queryKey: ['audit-location', id],
    queryFn: () => auditApi.getByLocation(id!),
    enabled: !!id && activeTab === 'activity' && canViewAudit,
  });

  const setTab = (tabId: string) => setSearchParams({ tab: tabId });
  const tabs = canViewAudit ? TABS : TABS.filter((t) => t.id !== 'activity');

  const totalUnits = stock?.reduce((s, i) => s + i.quantityOnShelf, 0) ?? 0;
  const unpaidCommission = sales?.filter((s) => !s.isPaidToLocation).reduce((s, sale) => s + sale.locationCommission, 0) ?? 0;

  return (
    <>
      <DetailPageLayout
        backTo="/locations"
        backLabel="Locations"
        title={location?.name ?? 'Location'}
        subtitle={location?.address}
        isLoading={isLoading}
        notFound={isError || (!isLoading && !location)}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setTab}
        actions={
          location && (
            <Button variant="secondary" size="sm" onClick={() => setShowEdit(true)}>
              <Pencil className="w-4 h-4 mr-1" /> Edit
            </Button>
          )
        }
      >
        {location && activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card p-5 space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-warm-cream">Contact</h3>
              {location.address && (
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <span>{location.address}</span>
                </div>
              )}
              {location.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>{location.phone}</span>
                </div>
              )}
              {location.managerName && (
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span>{location.managerName}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm pt-2 border-t border-gray-200 dark:border-charcoal-700">
                <Percent className="w-4 h-4 text-amber-450" />
                <span>{location.commissionRate}% commission</span>
              </div>
            </div>
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-gray-900 dark:text-warm-cream">At a glance</h3>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-gray-500">SKUs on shelf</dt><dd>{stock?.length ?? 0}</dd></div>
                <div><dt className="text-gray-500">Total units</dt><dd>{totalUnits}</dd></div>
                <div><dt className="text-gray-500">Sales recorded</dt><dd>{sales?.length ?? 0}</dd></div>
                <div><dt className="text-gray-500">Unpaid commission</dt><dd>{formatCurrency(unpaidCommission)}</dd></div>
              </dl>
            </div>
          </div>
        )}

        {location && activeTab === 'stock' && (
          <StockTable items={stock ?? []} isLoading={stockLoading} showLocation={false} />
        )}

        {location && activeTab === 'sales' && (
          <HistoryTable
            rows={sales ?? []}
            isLoading={salesLoading}
            rowKey={(s) => s.id}
            columns={[
              { key: 'date', header: 'Date', render: (s) => formatDate(s.saleDate) },
              { key: 'product', header: 'Product', render: (s) => <EntityLink type="product" id={s.productId} label={s.productName} /> },
              { key: 'sku', header: 'Variant', render: (s) => <span className="mono text-sm">{s.productVariantSku}</span> },
              { key: 'qty', header: 'Qty', align: 'right', render: (s) => s.quantity },
              { key: 'total', header: 'Total', align: 'right', render: (s) => formatCurrency(s.totalSaleAmount) },
              { key: 'paid', header: 'Paid', render: (s) => (s.isPaidToLocation ? 'Yes' : 'No') },
            ]}
          />
        )}

        {location && activeTab === 'transfers' && (
          <HistoryTable
            rows={transfers ?? []}
            isLoading={transfersLoading}
            rowKey={(t) => t.id}
            columns={[
              { key: 'date', header: 'Date', render: (t) => formatDate(t.transferDate) },
              { key: 'product', header: 'Product', render: (t) => <EntityLink type="product" id={t.productId} label={t.productName} /> },
              { key: 'from', header: 'From', render: (t) => <EntityLink type="location" id={t.fromLocationId} label={t.fromLocationName} /> },
              { key: 'to', header: 'To', render: (t) => <EntityLink type="location" id={t.toLocationId} label={t.toLocationName} /> },
              { key: 'qty', header: 'Qty', align: 'right', render: (t) => t.quantity },
            ]}
          />
        )}

        {location && activeTab === 'activity' && canViewAudit && (
          <HistoryTable
            rows={auditLogs ?? []}
            rowKey={(e) => e.id}
            columns={[
              { key: 'date', header: 'When', render: (e) => formatDate(e.createdAt) },
              { key: 'action', header: 'Action', render: (e) => e.actionType },
              { key: 'desc', header: 'Description', render: (e) => e.description },
              { key: 'user', header: 'User', render: (e) => e.userName ?? '—' },
            ]}
          />
        )}
      </DetailPageLayout>

      {showEdit && location && (
        <AddLocationModal
          isOpen
          location={location}
          onClose={() => setShowEdit(false)}
          onSuccess={() => {
            setShowEdit(false);
            refetch();
          }}
        />
      )}
    </>
  );
}
